import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CFactsheetDisclaimer from '../../../components/CFactsheetDisclaimer';
import CLocalizedText from '../../../components/CLocalizedText';
import CPageModule from '../../../components/CPageModule';
import CSortDropdown from '../../../components/CSortDropdown';
import CStatefulContainer from '../../../components/CStatefulContainer';
import StatefulModuleDisabler from '../../../components/StatefulModuleDisabler';
import XDEmptyResult from '../../../components/alerts/XDEmptyResult';
import GhostPostGalleryLinkButton from '../../../components/dashboardContent/GhostPostGalleryLinkButton';
import Page from '../../../components/layout/Page';
import XDPaginator from '../../../components/layout/XDPaginator';
import XDResponsiveGridWrapper from '../../../components/layout/XDResponsiveGridWrapper';
import { ActionsEnumDeprecate } from '../../../enums/actionsEnumDeprecate';
import { PageRoutes } from '../../../enums/enums';
import { useAppDispatch } from '../../../hooks/hooks';
import type { GhostTagModel } from '../../../models/GhostPostModel';
import type { GhostPostSummaryList } from '../../../models/GhostPostSummaryListModel';
import type { IRequestState } from '../../../models/IRequestState';
import { SvNewsService } from '../../../services/SvNewsService';
import { updateActiveAction, updateSnackbar } from '../../../store/appSlice';
import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';

interface State {
  posts?: GhostPostSummaryList;
  postsReqState: IRequestState;
  tags?: GhostTagModel[];
}

export default function ArticlesIndex() {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<State>({
    postsReqState: { isLoading: true, isError: false }
  });

  const updateState = (obj: Partial<State>) => {
    setState((prevState) => ({ ...prevState, ...obj }));
  };

  const { posts, tags, postsReqState } = state;

  const sortNewestText = CLocalizedTextStringStatic('newsSortNewest');
  const sortOldestText = CLocalizedTextStringStatic('newsSortOldest');

  const clickedOnPost = () => {
    dispatch(updateActiveAction(ActionsEnumDeprecate.n_post_stateful));
  };

  const fetchPosts = async (page: number, tag?: string, sortBy?: string) => {
    updateState({
      postsReqState: { ...postsReqState, isLoading: true }
    });

    const res = await SvNewsService.getAllPosts(page.toString(), tag, sortBy);

    if (!(await res.isSuccess())) {
      updateState({
        postsReqState: { isLoading: false, isError: true }
      });

      dispatch(
        updateSnackbar({
          severity: 'error',
          message: await res.getMessage(),
          isVisible: true
        })
      );
      return;
    }

    const data = await res.getData();

    updateState({
      posts: data,
      postsReqState: { isLoading: false, isError: false }
    });

    if (data && data.meta.pagination.pages > 0 && data.meta.pagination.page > data.meta.pagination.pages) {
      await fetchPosts(1, tag, sortBy);
    }
  };

  const clickedOnTagHandler = (tag: string, pg: number) => {
    const currentSort = searchParams.get('sortBy') ?? 'newest';
    setSearchParams({ tag, page: pg.toString(), sortBy: currentSort });
  };

  const loadTags = async () => {
    const res = await SvNewsService.getPublicTags();

    if (!(await res.isSuccess())) {
      dispatch(
        updateSnackbar({
          severity: 'error',
          message: await res.getMessage(),
          isVisible: true
        })
      );
      return;
    }

    updateState({ tags: (await res.getData()) ?? [] });
  };

  useEffect(() => {
    let currentPage = Number.parseInt(searchParams.get('page') ?? '1');

    if (Number.isNaN(currentPage)) {
      currentPage = 1;
    }

    const sortBy = searchParams.get('sortBy') ?? 'newest';

    (async () => {
      await fetchPosts(currentPage, searchParams.get('tag') ?? '', sortBy);
    })();
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      await loadTags();
    })();
  }, []);

  const shouldRenderDisclaimer =
    !postsReqState.isLoading &&
    !postsReqState.isError &&
    (posts?.posts?.length ?? 0) > 0;

  return (
    <Page>
      <CPageModule>
        <h1>
          <CLocalizedText dictKey={'newsArticles'} />
        </h1>
      </CPageModule>
      <CPageModule style={{ paddingTop: 0 }}>
        {posts && (
          <StatefulModuleDisabler disabled={postsReqState.isLoading}>
            <div className={'flex gap-4 flex-col flex-grow'}>
              <div className={'flex gap-2 flex-col'}>
                <p className={'uppercase text-sm font-bold mt-1 opacity-80 -mb-2'}>
                  <CLocalizedText dictKey={'newsFilterByTag'} />
                </p>
                <div
                  className={'flex items-center flex-wrap gap-y-4 gap-x-4'}
                  style={{ justifyContent: 'space-between' }}
                >
                  <div
                    className={'flex gap-5 gap-y-2 md:gap-y-1 items-center flex-wrap text-xl md:text-lg'}
                    style={{ minWidth: 'min-content' }}
                  >
                    <TagButton
                      slug={''}
                      label={<CLocalizedText dictKey={'newsTagAll'} />}
                      isSelected={(searchParams.get('tag') ?? '') === ''}
                      onClick={() => {
                        clickedOnTagHandler('', 1);
                      }}
                    />

                    {tags?.map((tagItem) => (
                      <TagButton
                        key={tagItem.slug}
                        isSelected={searchParams.get('tag') === tagItem.slug}
                        label={tagItem.name}
                        slug={tagItem.slug}
                        onClick={() => {
                          clickedOnTagHandler(tagItem.slug, 1);
                        }}
                      />
                    ))}
                  </div>
                  <div className={'ml-auto'} style={{ minWidth: 'min-content' }}>
                    <CSortDropdown
                      onChange={async (sortBy) => {
                        const currentTag = searchParams.get('tag') ?? '';
                        const currentPage = searchParams.get('page') ?? '1';

                        setSearchParams({
                          tag: currentTag,
                          page: currentPage,
                          sortBy
                        });
                      }}
                      options={{
                        newest: sortNewestText,
                        oldest: sortOldestText
                      }}
                      defaultKey={searchParams.get('sortBy') ?? 'newest'}
                    />
                  </div>
                </div>
              </div>
            </div>
          </StatefulModuleDisabler>
        )}

        {posts?.posts && posts.posts.length === 0 && <XDEmptyResult />}

        <CStatefulContainer apiRequestState={postsReqState}>
          <XDResponsiveGridWrapper className={'gap-[0px]'} borderlessContent>
            <>
              {posts?.posts?.map((post, index) => (
                <GhostPostGalleryLinkButton
                  key={index}
                  post={post}
                  pathPrefix={`../../${PageRoutes.NEWS_OUTLET}/${PageRoutes.NEWS_ARTICLES_OUTLET}`}
                  onClick={() => {
                    clickedOnPost();
                  }}
                />
              ))}
            </>
          </XDResponsiveGridWrapper>
        </CStatefulContainer>

        <XDPaginator
          apiReqState={postsReqState}
          pagination={posts?.meta.pagination}
          retrievePageCallback={(pageNumber) => {
            setSearchParams({
              ...Object.fromEntries([...searchParams]),
              page: pageNumber.toString()
            });
          }}
        />
      </CPageModule>
      {shouldRenderDisclaimer && <CFactsheetDisclaimer />}
    </Page>
  );
}

interface TagButtonProps {
  slug: string;
  label: string | JSX.Element;
  isSelected: boolean;
  onClick: (slug: string) => void;
}

function TagButton({ slug, label, isSelected, onClick }: TagButtonProps) {
  return (
    <a
      className={`mb-0 min-h-[40px] flex items-center pb-1 ${isSelected ? 'text-teal-ppt-1' : 'font-normal'}`}
      onClick={() => {
        onClick(slug);
      }}
    >
      {label}
    </a>
  );
}
